import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { ExternalLink, Play, MapPin, Star, Users, Heart, Share2, Clock, Globe } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from '@/components/ui/badge';
import { SEO } from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { ShareCollectionDialog } from '@/components/collections/ShareCollectionDialog';

export default function SharedCollection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showShareDialog, setShowShareDialog] = React.useState(false);

  const { data: playlist, isLoading: isPlaylistLoading } = useQuery({
    queryKey: ['shared-playlist', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlists')
        .select(`
          *,
          created_by_user:created_by_user_id(
            id,
            username,
            avatar_url
          )
        `)
        .eq('id', id)
        .eq('is_public', true)
        .maybeSingle();
      
      if (error) throw error;
      console.log('Playlist data:', data);
      return data;
    }
  });

  const { data: places, isLoading: isPlacesLoading } = useQuery({
    queryKey: ['shared-playlist-items', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlist_items')
        .select('*')
        .eq('playlist_id', id)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      console.log('Places data:', data, 'Count:', data?.length);
      return data;
    }
  });

  const handlePlaceClick = (place: any) => {
    // Navigate to the place page instead of opening a modal
    navigate(`/places/${place.id}`);
  };

  // Create properly formatted collection object for ShareCollectionDialog
  const formattedCollection = React.useMemo(() => {
    console.log('Creating formattedCollection with:', { 
      playlist: !!playlist, 
      places: !!places, 
      placesLength: places?.length,
      placesData: places
    });
    
    if (!playlist) {
      console.log('No playlist data for formattedCollection');
      return null;
    }
    
    // Create place_items array with proper structure - always an array, never undefined
    const placeItems = Array.isArray(places) ? places.map(place => ({
      id: place.id,
      name: place.name || 'Unnamed Place',
      place_type: place.place_type || 'place',
      photos: place.photos || [],
      rating: place.rating || 0,
      address: place.address || ''
    })) : [];
    
    // Get the actual count
    const actualCount = Array.isArray(places) ? places.length : 0;
    
    const formatted = {
      ...playlist,
      places_count: actualCount,
      place_items: placeItems
    };
    
    console.log('Final formatted collection:', {
      id: formatted.id,
      name: formatted.name,
      places_count: formatted.places_count,
      place_items_length: formatted.place_items?.length,
      place_items: formatted.place_items,
      place_items_sample: formatted.place_items?.slice(0, 2)
    });
    
    return formatted;
  }, [playlist, places]);

  // Debug what's being passed to the dialog
  React.useEffect(() => {
    if (showShareDialog && formattedCollection) {
      console.log('About to show ShareCollectionDialog with collection:', {
        id: formattedCollection.id,
        name: formattedCollection.name,
        places_count: formattedCollection.places_count,
        place_items: formattedCollection.place_items,
        place_items_length: formattedCollection.place_items?.length,
        full_object: formattedCollection
      });
    }
  }, [showShareDialog, formattedCollection]);

  if (isPlaylistLoading || isPlacesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto py-8">
          <div className="animate-pulse max-w-6xl mx-auto">
            {/* Hero skeleton */}
            <div className="bg-gradient-to-r from-gray-200 to-gray-300 h-80 rounded-3xl mb-8"></div>
            {/* Content skeleton */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <MapPin className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Collection not found</h2>
          <p className="text-muted-foreground mb-6">This collection might be private or doesn't exist anymore.</p>
          <Button onClick={() => navigate('/collections')} className="bg-gradient-to-r from-primary to-purple-600">
            <Globe className="w-4 h-4 mr-2" />
            Browse Collections
          </Button>
        </div>
      </div>
    );
  }

  const firstPlaceImage = places?.[0]?.photos?.[0];
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long' 
  });

  // Enhanced SEO with collection-specific data
  const seoTitle = `${playlist?.name || 'Collection'} - Travel Collection | ${places?.length || 0} Places${playlist?.city ? ` in ${playlist.city}` : ''}`;
  const seoDescription = playlist?.description 
    ? `${playlist.description} Explore ${places?.length || 0} handpicked places in this curated travel collection.`
    : `Discover ${places?.length || 0} amazing places in ${playlist?.city || 'this destination'}. A curated travel collection with local insights and recommendations.`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        image={firstPlaceImage || '/og-image.png'}
        type="website"
        keywords={[
          'travel collection',
          playlist?.city || '',
          playlist?.country || '',
          'travel guide',
          'destinations',
          'places to visit'
        ].filter(Boolean)}
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Enhanced Hero Section */}
          <div className="relative overflow-hidden rounded-3xl mb-8">
            <div 
              className="absolute inset-0 bg-gradient-to-r from-primary/90 to-purple-600/90"
              style={{
                backgroundImage: firstPlaceImage ? `url(${firstPlaceImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundBlendMode: 'overlay'
              }}
            />
            <div className="relative p-8 md:p-12 text-white">
              <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
                {/* Collection Cover */}
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 md:w-56 md:h-56 bg-white/20 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/30">
                    {firstPlaceImage ? (
                      <img 
                        src={firstPlaceImage} 
                        alt={playlist?.name || 'Collection'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/20 to-white/5">
                        <MapPin className="w-16 h-16 text-white/80" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Collection Info */}
                <div className="flex-grow min-w-0">
                  <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
                    Travel Collection
                  </Badge>
                  <h1 className="text-4xl md:text-6xl font-bold mb-4 break-words">
                    {playlist?.name || 'Collection'}
                  </h1>
                  {playlist?.description && (
                    <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed max-w-2xl">
                      {playlist.description}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
                    {playlist?.created_by_user && (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6 border border-white/30">
                          <AvatarImage src={playlist.created_by_user?.avatar_url || ''} />
                          <AvatarFallback className="bg-white/20 text-white text-xs">
                            {playlist.created_by_user?.username?.[0]?.toUpperCase() || 'A'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{playlist.created_by_user?.username || 'Anonymous'}</span>
                      </div>
                    )}
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{places?.length || 0} places</span>
                    </div>
                    {playlist?.city && (
                      <>
                        <span>•</span>
                        <span>{playlist.city}{playlist.country && `, ${playlist.country}`}</span>
                      </>
                    )}
                    {playlist?.followers_count && playlist.followers_count > 0 && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{playlist.followers_count} followers</span>
                        </div>
                      </>
                    )}
                    {playlist?.created_at && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(playlist.created_at)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={() => navigate(`/collections/${playlist?.name?.toLowerCase().replace(/\s+/g, '-')}/${id}`)}
                      size="lg"
                      className="bg-white text-primary hover:bg-white/90 font-semibold"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Explore Collection
                    </Button>
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm"
                      onClick={() => {
                        console.log('Share button clicked, formattedCollection before dialog:', formattedCollection);
                        setShowShareDialog(true);
                      }}
                      disabled={!formattedCollection}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Places Grid */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Featured Places</h2>
              <Badge variant="outline" className="text-sm">
                {places?.length || 0} destinations
              </Badge>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {places?.map((place, index) => (
                <Card 
                  key={place.id}
                  className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-0 shadow-md overflow-hidden cursor-pointer"
                  onClick={() => handlePlaceClick(place)}
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                        {place.photos?.[0] ? (
                          <img 
                            src={place.photos[0]} 
                            alt={place.name || 'Place'}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPin className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-white/90 text-gray-700 backdrop-blur-sm">
                          #{index + 1}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                        {place.name || 'Unnamed Place'}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 capitalize">
                        {place.place_type?.replace('_', ' ') || 'Place'}
                        {place.address && (
                          <span className="block text-xs truncate">{place.address}</span>
                        )}
                      </p>
                      {place.rating && place.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium">{place.rating.toFixed(1)}</span>
                          {place.rating_count && place.rating_count > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({place.rating_count})
                            </span>
                          )}
                        </div>
                      )}
                      {place.note && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                          {place.note}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {places && places.length > 6 && (
              <div className="text-center mt-8">
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(`/collections/${playlist?.name?.toLowerCase().replace(/\s+/g, '-')}/${id}`)}
                  className="bg-white hover:bg-gray-50"
                >
                  View All {places.length} Places
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="mt-12 bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-2xl p-8 text-center border border-primary/20">
            <h3 className="text-2xl font-bold mb-4">Start Your Own Travel Journey</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Create your own collections, discover amazing destinations, and connect with fellow travelers on Travilis.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button 
                onClick={() => navigate('/collections')}
                className="bg-gradient-to-r from-primary to-purple-600"
              >
                <Globe className="w-4 h-4 mr-2" />
                Browse More Collections
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/auth')}
              >
                Join Travilis
              </Button>
            </div>
          </div>
        </div>
      </div>

      {formattedCollection && (
        <ShareCollectionDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          collection={formattedCollection}
        />
      )}
    </div>
  );
}
